import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { getPlatformLogo } from './types';
import { platformsData } from './platformsData';

interface PlatformComparisonHeaderProps {
  availablePlatforms: string[];
  selectedPlatforms: string[];
  onPlatformsChange: (platforms: string[]) => void;
  maxPlatforms?: number;
}

export function PlatformComparisonHeader({ 
  availablePlatforms, 
  selectedPlatforms, 
  onPlatformsChange,
  maxPlatforms = 4
}: PlatformComparisonHeaderProps) {
  useLanguage();
  const getPlatformName = (platformId: string) => {
    const platform = platformsData.find(p => p.id === platformId);
    return platform?.name || platformId;
  };

  const handleAddPlatform = (platformId: string) => {
    if (!selectedPlatforms.includes(platformId) && selectedPlatforms.length < maxPlatforms) {
      onPlatformsChange([...selectedPlatforms, platformId]);
    }
  };

  const handleRemovePlatform = (platformId: string) => {
    onPlatformsChange(selectedPlatforms.filter(p => p !== platformId));
  };

  const unselectedPlatforms = availablePlatforms.filter(
    p => !selectedPlatforms.includes(p)
  );

  // Create slots based on selected platforms + add button if available
  const hasMorePlatforms = unselectedPlatforms.length > 0;
  const shouldShowAddButton = hasMorePlatforms && selectedPlatforms.length < maxPlatforms;
  
  // Calculate number of slots to show
  const numSlots = shouldShowAddButton 
    ? selectedPlatforms.length + 1 
    : selectedPlatforms.length;

  return (
    <div className="w-full">
      {/* Linha horizontal com chips pequenos */}
      <div className="flex items-center gap-3 overflow-x-auto pb-2">
        {/* Platform Chips */}
        {selectedPlatforms.map((platformId) => (
          <div
            key={platformId}
            className="inline-flex items-center gap-2.5 px-3 py-1.5 bg-white border border-[#DCE6F9] rounded-[10px] whitespace-nowrap flex-shrink-0"
          >
            <img 
              src={getPlatformLogo(platformId)} 
              alt={getPlatformName(platformId)}
              className="h-5 w-auto object-contain"
            />
            <span className="text-[13px] font-medium text-[#475467]">
              {getPlatformName(platformId)}
            </span>
            <button
              onClick={() => handleRemovePlatform(platformId)}
              className="text-[#8A94A6] hover:text-[#EF4444] transition-colors ml-1"
              aria-label={translateInterfaceText("Remover plataforma")}
            >
              <Plus className="h-3.5 w-3.5 rotate-45" />
            </button>
          </div>
        ))}
        
        {/* Add Platform Button */}
        {shouldShowAddButton && (
          <Select onValueChange={handleAddPlatform}>
            <SelectTrigger 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-dashed border-[#2563EB] text-[#2563EB] rounded-[10px] hover:bg-[#F4F8FF] transition-colors whitespace-nowrap flex-shrink-0 h-auto"
            >
              <Plus className="h-4 w-4" />
              <span className="text-[13px] font-medium">{translateInterfaceText("Adicionar Plataforma")}</span>
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              {unselectedPlatforms.map((platformId) => (
                <SelectItem key={platformId} value={platformId}>
                  <div className="flex items-center justify-center py-2">
                    <img 
                      src={getPlatformLogo(platformId)} 
                      alt={getPlatformName(platformId)}
                      className="max-w-[100px] max-h-[40px] object-contain"
                    />
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
